import { useState, useMemo } from "react";
import {
  Plus, MapPin, Wrench, Download, ChevronDown, ChevronUp, Filter, X,
  MessageCircle, Send, Eye,
} from "lucide-react";
import GuideBanner from "../components/GuideBanner";
import Badge from "../components/Badge";
import Pagination from "../components/Pagination";
import { PARTNER_STAGES, PARTNER_TYPES, BRAZILIAN_STATES } from "../constants";
import { unificarRede, tipoDaEmpresa, rotuloTipo, tipoSugeridoPeloNome } from "../lib/rede";
import { exportCSV, waLink, getToday, fmtDate } from "../utils";

const stateLabel = (uf) => {
  const s = BRAZILIAN_STATES.find((x) => x.uf === uf);
  return s ? `${s.uf} — ${s.name}` : uf;
};

const IN_PROGRESS = ["identificado", "abordagem", "documentacao", "validacao", "ativacao"];
const FORA = ["sem_interesse", "recusado"];

const PERIODOS = [
  { id: "all", label: "Qualquer data" },
  { id: "hoje", label: "Hoje" },
  { id: "7", label: "Últimos 7 dias" },
  { id: "30", label: "Últimos 30 dias" },
];
const diasAtras = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().split("T")[0]; };

export default function Partners({ partners, credenciamentos = [], search, today, setShowNewPartner, setSelPartner, onSendLink }) {
  const [statusView, setStatusView] = useState("all"); // KPI: all|ativo|andamento|fora
  const [typeFilter, setTypeFilter] = useState("all");
  const [stageFilter, setStageFilter] = useState("all");
  const [ufFilter, setUfFilter] = useState("all");
  const [enderecoFilter, setEnderecoFilter] = useState("");
  const [contatoFilter, setContatoFilter] = useState("");
  const [servicoFilter, setServicoFilter] = useState("");
  const [periodoFilter, setPeriodoFilter] = useState("all");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("recente");
  const [showFilters, setShowFilters] = useState(false);
  const [showPipeline, setShowPipeline] = useState(false);
  const [expandedStage, setExpandedStage] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const toggleStage = (id) => setExpandedStage((p) => (p === id ? null : id));

  // ── KPIs ──
  const kpis = useMemo(() => {
    const mes = getToday().slice(0, 7);
    let ativos = 0, andamento = 0, fora = 0, mesCount = 0;
    for (const p of partners) {
      if (p.stage === "ativo") ativos++;
      else if (IN_PROGRESS.includes(p.stage)) andamento++;
      if (FORA.includes(p.stage)) fora++;
      if ((p.createdAt || "").slice(0, 7) === mes) mesCount++;
    }
    return { total: partners.length, ativos, andamento, fora, mesCount };
  }, [partners]);

  const ufOptions = useMemo(() => Array.from(new Set((partners || []).map((p) => p.region).filter(Boolean))).sort(), [partners]);

  // A ficha sabe mais do ramo do que o cadastro do parceiro: ela é preenchida
  // com a atividade que vem do CNPJ. A mesma junção da tela de Fichas — repetir
  // a regra aqui abriria espaço para as duas discordarem.
  const fichaDoParceiro = useMemo(() => {
    const mapa = {};
    for (const item of unificarRede(partners, credenciamentos)) {
      if (item.parceiro && item.ficha) mapa[item.parceiro.id] = item.ficha;
    }
    return mapa;
  }, [partners, credenciamentos]);

  const filtered = useMemo(() => {
    setPage(1);
    const texto = (q || search || "").toLowerCase().trim();
    const cutoff = periodoFilter === "hoje" ? getToday() : periodoFilter !== "all" ? diasAtras(Number(periodoFilter)) : null;
    const list = partners.filter((p) => {
      if (statusView === "ativo" && p.stage !== "ativo") return false;
      if (statusView === "andamento" && !IN_PROGRESS.includes(p.stage)) return false;
      if (statusView === "fora" && !FORA.includes(p.stage)) return false;
      if (typeFilter !== "all" && p.type !== typeFilter) return false;
      if (stageFilter !== "all" && p.stage !== stageFilter) return false;
      if (ufFilter !== "all" && p.region !== ufFilter) return false;
      if (enderecoFilter && !(p.address || "").toLowerCase().includes(enderecoFilter.toLowerCase())) return false;
      if (contatoFilter && !(p.contact || "").toLowerCase().includes(contatoFilter.toLowerCase())) return false;
      if (servicoFilter && !(p.services || "").toLowerCase().includes(servicoFilter.toLowerCase())) return false;
      if (cutoff) { const cd = p.createdAt || ""; if (periodoFilter === "hoje" ? cd !== cutoff : cd < cutoff) return false; }
      if (texto) {
        const hay = `${p.name} ${p.cnpj || ""} ${p.contact || ""} ${p.razaoSocial || ""} ${p.nomeFantasia || ""}`.toLowerCase();
        if (!hay.includes(texto)) return false;
      }
      return true;
    });
    list.sort((a, b) => {
      const av = `${a.createdAt || ""}${a.id}`, bv = `${b.createdAt || ""}${b.id}`;
      return sort === "recente" ? bv.localeCompare(av) : av.localeCompare(bv);
    });
    return list;
  }, [partners, statusView, typeFilter, stageFilter, ufFilter, enderecoFilter, contatoFilter, servicoFilter, periodoFilter, q, search, sort]);

  const paginated = useMemo(() => { const s = (page - 1) * pageSize; return filtered.slice(s, s + pageSize); }, [filtered, page, pageSize]);

  const handleExport = () => {
    const data = filtered.map((p) => ({
      Nome: p.name, Tipo: rotuloTipo(tipoDaEmpresa(p, fichaDoParceiro[p.id])), Contato: p.contact,
      Telefone: p.phone, Email: p.email || "", UF: p.region, Endereço: p.address || "",
      Estágio: PARTNER_STAGES.find((s) => s.id === p.stage)?.label || "", Serviços: p.services || "",
      CNPJ: p.cnpj || "", Criado: fmtDate(p.createdAt), "Último Contato": fmtDate(p.lastContact),
    }));
    exportCSV(data, "parceiros_instasolutions");
  };

  const limparFiltros = () => { setTypeFilter("all"); setStageFilter("all"); setUfFilter("all"); setEnderecoFilter(""); setContatoFilter(""); setServicoFilter(""); setPeriodoFilter("all"); setQ(""); };
  const filtrosAtivos = typeFilter !== "all" || stageFilter !== "all" || ufFilter !== "all" || enderecoFilter || contatoFilter || servicoFilter || periodoFilter !== "all" || q;

  const sel = { padding: "7px 10px", borderRadius: 7, border: "1px solid #d1d5db", fontSize: 12, background: "#fff", cursor: "pointer", fontFamily: "inherit", width: "100%" };
  const inp = { ...sel, cursor: "text" };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <GuideBanner
        title="Frente 2 — Credenciamento de Parceiros (Contínua)"
        text="Gerencie oficinas, funilarias, borracharias e postos. Esta frente é PERMANENTE — continue expandindo a rede, focando em regiões sem cobertura."
      />

      {/* ── Painel de indicadores ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
        <KpiCard label="Total de parceiros" value={kpis.total} color="#7c3aed" active={statusView === "all"} onClick={() => setStatusView("all")} />
        <KpiCard label="Ativos na rede" value={kpis.ativos} color="#059669" active={statusView === "ativo"} onClick={() => setStatusView("ativo")} />
        <KpiCard label="Em andamento" value={kpis.andamento} color="#3b82f6" active={statusView === "andamento"} onClick={() => setStatusView("andamento")} />
        <KpiCard label="Fora do fluxo" value={kpis.fora} color="#f59e0b" active={statusView === "fora"} onClick={() => setStatusView("fora")} />
        <KpiCard label="Novos este mês" value={kpis.mesCount} color="#0d9488" />
      </div>

      {/* ── Barra de ações ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          <button onClick={() => setShowFilters((v) => !v)} style={{ display: "flex", alignItems: "center", gap: 5, padding: "7px 14px", borderRadius: 7, border: `1px solid ${filtrosAtivos ? "#7c3aed" : "#d1d5db"}`, background: filtrosAtivos ? "#f5f3ff" : "#fff", color: filtrosAtivos ? "#6d28d9" : "#475569", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
            <Filter size={13} /> Filtros{filtrosAtivos ? " •" : ""}
          </button>
          <select value={sort} onChange={(e) => setSort(e.target.value)} style={{ ...sel, width: "auto" }}>
            <option value="recente">Mais recentes</option>
            <option value="antigo">Mais antigos</option>
          </select>
          <button onClick={() => setShowPipeline((v) => !v)} style={{ padding: "7px 14px", borderRadius: 7, border: "1px solid #d1d5db", background: "#fff", color: "#475569", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
            {showPipeline ? "Ocultar" : "Ver"} pipeline
          </button>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={handleExport} style={{ display: "flex", alignItems: "center", gap: 5, padding: "7px 14px", borderRadius: 7, border: "1px solid #d1d5db", background: "#fff", color: "#475569", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
            <Download size={13} /> CSV
          </button>
          {onSendLink && (
            <button onClick={() => onSendLink(null)} style={{ display: "flex", alignItems: "center", gap: 5, padding: "7px 14px", borderRadius: 7, border: "1px solid #7c3aed", background: "#f5f3ff", color: "#6d28d9", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
              <Send size={13} /> Enviar formulário
            </button>
          )}
          <button onClick={() => setShowNewPartner(true)} style={{ display: "flex", alignItems: "center", gap: 5, padding: "8px 14px", borderRadius: 7, border: "none", background: "#7c3aed", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
            <Plus size={14} /> Novo Parceiro
          </button>
        </div>
      </div>

      {/* ── Quadro de filtros ── */}
      {showFilters && (
        <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e5e7eb", padding: 14 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
            <div><Lbl>Buscar (nome, CNPJ)</Lbl><input style={inp} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nome, CNPJ..." /></div>
            <div><Lbl>Tipo</Lbl><select style={sel} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}><option value="all">Todos</option>{PARTNER_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}</select></div>
            <div><Lbl>Estágio</Lbl><select style={sel} value={stageFilter} onChange={(e) => setStageFilter(e.target.value)}><option value="all">Todos</option>{PARTNER_STAGES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}</select></div>
            <div><Lbl>Estado (UF)</Lbl><select style={sel} value={ufFilter} onChange={(e) => setUfFilter(e.target.value)}><option value="all">Todos</option>{(ufOptions.length ? ufOptions : BRAZILIAN_STATES.map((s) => s.uf)).map((uf) => <option key={uf} value={uf}>{uf}</option>)}</select></div>
            <div><Lbl>Cidade / Endereço</Lbl><input style={inp} value={enderecoFilter} onChange={(e) => setEnderecoFilter(e.target.value)} placeholder="Cidade, bairro..." /></div>
            <div><Lbl>Contato</Lbl><input style={inp} value={contatoFilter} onChange={(e) => setContatoFilter(e.target.value)} placeholder="Responsável" /></div>
            <div><Lbl>Serviço prestado</Lbl><input style={inp} value={servicoFilter} onChange={(e) => setServicoFilter(e.target.value)} placeholder="Ex: freios, óleo..." /></div>
            <div><Lbl>Data de cadastro</Lbl><select style={sel} value={periodoFilter} onChange={(e) => setPeriodoFilter(e.target.value)}>{PERIODOS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}</select></div>
          </div>
          {filtrosAtivos && (
            <div style={{ marginTop: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 11, color: "#64748b" }}>{filtered.length} resultado(s)</span>
              <button onClick={limparFiltros} style={{ display: "flex", alignItems: "center", gap: 4, padding: "5px 10px", borderRadius: 6, border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", fontSize: 11, fontWeight: 600, cursor: "pointer" }}><X size={12} /> Limpar filtros</button>
            </div>
          )}
        </div>
      )}

      {/* ── Pipeline por estágio (colapsável) ── */}
      {showPipeline && (
        <div style={{ background: "#fff", borderRadius: 10, padding: "16px 18px", border: "1px solid #e5e7eb" }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Pipeline Parceiros — Clique no estágio para ver os cards</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {PARTNER_STAGES.filter((s) => !FORA.includes(s.id)).map((s) => {
              const count = (partners || []).filter((p) => p.stage === s.id).length;
              const isExpanded = expandedStage === s.id;
              return (
                <div key={s.id} style={{ flex: "1 1 auto", minWidth: 0 }}>
                  <div onClick={() => toggleStage(s.id)} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 12px", borderRadius: 8, cursor: "pointer", background: isExpanded ? s.color + "15" : "#f8fafc", border: `2px solid ${isExpanded ? s.color : "#e2e8f0"}` }}>
                    <div style={{ width: 8, height: 8, borderRadius: 2, background: s.color, flexShrink: 0 }} />
                    <span style={{ fontSize: 11, fontWeight: 600, color: "#1e293b", whiteSpace: "nowrap" }}>{s.label}</span>
                    <span style={{ fontSize: 12, fontWeight: 800, color: s.color, marginLeft: "auto" }}>{count}</span>
                    {isExpanded ? <ChevronUp size={12} color="#64748b" /> : <ChevronDown size={12} color="#64748b" />}
                  </div>
                </div>
              );
            })}
          </div>
          {PARTNER_STAGES.filter((s) => !FORA.includes(s.id)).map((s) => {
            if (expandedStage !== s.id) return null;
            const stagePartners = (partners || []).filter((p) => p.stage === s.id);
            return (
              <div key={s.id} style={{ marginTop: 10, padding: 10, background: "#f8fafc", borderRadius: 8, border: `1px solid ${s.color}30` }}>
                {stagePartners.length === 0 ? (
                  <div style={{ fontSize: 12, color: "#94a3b8", textAlign: "center", padding: 12 }}>Nenhum parceiro neste estágio</div>
                ) : (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 8 }}>
                    {stagePartners.map((p) => {
                      const type = PARTNER_TYPES.find((t) => t.id === p.type);
                      return (
                        <div key={p.id} onClick={() => setSelPartner(p)} style={{ background: "#fff", borderRadius: 7, padding: 10, cursor: "pointer", border: "1px solid #e5e7eb" }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: "#0f172a" }}>{p.name}</div>
                          <div style={{ fontSize: 10, color: "#64748b", marginTop: 2 }}>{p.contact || "Sem contato"}</div>
                          <div style={{ fontSize: 9, color: "#94a3b8", marginTop: 3 }}>{stateLabel(p.region)}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Cards ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(290px, 1fr))", gap: 10 }}>
        {paginated.length === 0 && (
          <div style={{ gridColumn: "1/-1", padding: "30px", textAlign: "center", color: "#94a3b8", fontSize: 12 }}>Nenhum parceiro encontrado com os filtros atuais</div>
        )}
        {paginated.map((p) => {
          const tipoResolvido = tipoDaEmpresa(p, fichaDoParceiro[p.id]);
          const tipoLabel = rotuloTipo(tipoResolvido);
          // O nome diz uma coisa e o cadastro diz "oficina" (o padrão de quem
          // nunca escolheu). Marca para alguém conferir — não troca sozinho.
          const tipoSuspeito = tipoSugeridoPeloNome(p.name, tipoResolvido);
          const stage = PARTNER_STAGES.find((s) => s.id === p.stage);
          const wa = waLink(p.phone);
          return (
            <div key={p.id} style={{ background: "#fff", borderRadius: 10, padding: "14px 16px", border: "1px solid #e5e7eb", borderLeft: `4px solid ${stage?.color || "#ccc"}` }}>
              <div onClick={() => setSelPartner(p)} style={{ cursor: "pointer" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, gap: 8 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</div>
                    {/* Sem tipo conhecido não se afirma nada: dizer "Oficina Mecânica" para
                        uma empresa que ninguém classificou é pior do que não dizer. */}
                    <div style={{ fontSize: 10, color: tipoLabel ? "#64748b" : "#b45309" }}>
                      {tipoLabel || "Tipo não informado"}{p.contact ? ` — ${p.contact}` : ""}
                      {tipoSuspeito && (
                        <span
                          title={`O nome sugere ${rotuloTipo(tipoSuspeito)}, mas o cadastro está como Oficina Mecânica. Abra e confirme o tipo.`}
                          style={{ marginLeft: 6, color: "#b45309", fontWeight: 700, cursor: "help" }}
                        >
                          · confira o tipo
                        </span>
                      )}
                    </div>
                  </div>
                  <Badge color={stage?.color}>{stage?.label}</Badge>
                </div>
                <div style={{ fontSize: 11, color: "#475569", marginBottom: 4 }}>
                  <MapPin size={10} style={{ verticalAlign: "-1px", marginRight: 3 }} />{p.address || stateLabel(p.region)}
                </div>
                {p.services && (
                  <div style={{ fontSize: 11, color: "#475569" }}>
                    <Wrench size={10} style={{ verticalAlign: "-1px", marginRight: 3 }} />{p.services.slice(0, 60)}{p.services.length > 60 ? "..." : ""}
                  </div>
                )}
              </div>
              {/* Ações rápidas */}
              <div style={{ display: "flex", gap: 6, marginTop: 10, paddingTop: 10, borderTop: "1px solid #f1f5f9", flexWrap: "wrap" }}>
                <button onClick={() => setSelPartner(p)} style={actBtn(false)} title="Abrir / editar"><Eye size={12} /> Detalhe</button>
                <button onClick={() => wa && window.open(wa, "_blank")} disabled={!wa} style={{ ...actBtn(!wa), color: wa ? "#059669" : "#cbd5e1", borderColor: wa ? "#a7f3d0" : "#e2e8f0" }} title={wa ? "WhatsApp" : "Sem telefone"}><MessageCircle size={12} /></button>
                {onSendLink && <button onClick={() => onSendLink(p)} style={{ ...actBtn(false), color: "#6d28d9", borderColor: "#e9d5ff" }} title="Enviar formulário de credenciamento"><Send size={12} /> Formulário</button>}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e5e7eb" }}>
        <Pagination page={page} totalItems={filtered.length} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={setPageSize} />
      </div>
    </div>
  );
}

function KpiCard({ label, value, color, active, onClick }) {
  return (
    <div onClick={onClick} style={{ background: "#fff", borderRadius: 10, padding: "12px 14px", border: `1px solid ${active ? color : "#e5e7eb"}`, borderLeft: `4px solid ${color}`, cursor: onClick ? "pointer" : "default", boxShadow: active ? `0 0 0 1px ${color}` : "none" }}>
      <div style={{ fontSize: 22, fontWeight: 800, color }}>{value}</div>
      <div style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>{label}</div>
    </div>
  );
}
function Lbl({ children }) {
  return <div style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>{children}</div>;
}
const actBtn = (disabled) => ({
  display: "flex", alignItems: "center", gap: 4, padding: "6px 10px", borderRadius: 6,
  border: "1px solid #e2e8f0", background: "#fff", color: disabled ? "#cbd5e1" : "#475569",
  fontSize: 11, fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer",
});
